import React from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { MuiPickersUtilsProvider, KeyboardTimePicker, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { getNextDay } from "@icarius-utils/date";
import Loader from "@icarius-common/loader";
import { createAssistanceMarkAction, updateAssistanceMarkAction } from "../../actions";
import useHandleForm from "./useHandleForm";

const gridStyle = { padding: "10px" };

const AssistanceMarkDialog = (props) => {

  const {
    open,
    isLoading,
    data,
    openCalendarList,
    employeeList,
    handleClose,
  } = props;

  const dispatch = useDispatch();

  const create = (dataToSend) => {
    dispatch(createAssistanceMarkAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(updateAssistanceMarkAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const {
    formData,
    isCreate,
    hasOriginalEntryDate,
    isIncomplete,
    setFormValue,
    submit,
  } = useHandleForm(data, create, modify, openCalendarList, employeeList);

  const title = (() => {
    if (isCreate) return getLocalizedString("createAttendanceMark");
    if (isIncomplete) return getLocalizedString(
      "completeMarkTitle",
      {
        name: data["APELLIDO Y NOMBRE"],
        entryOrExit1: hasOriginalEntryDate ? getLocalizedString("salida") : getLocalizedString("entrada"),
        entryOrExit2: hasOriginalEntryDate ? getLocalizedString("entrada") : getLocalizedString("salida"),
        date: hasOriginalEntryDate ? data["FECHA DE ENTRADA"] : data["FECHA DE SALIDA"],
        time: hasOriginalEntryDate ? data["HORA DE ENTRADA"] : data["HORA DE SALIDA"]
      }
    );
    return getLocalizedString("markTitle", { name: data["APELLIDO Y NOMBRE"], date: data["FECHA DE ENTRADA"] });
  })();

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      fullWidth
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move', marginRight: 20 }} id="draggable-dialog-title">
          {title}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent style={{ paddingTop: 0 }}>
          <Loader open={isLoading} />
          {
            isCreate &&
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <Autocomplete
                style={{ width: '100%' }}
                options={employeeList || []}
                value={formData.employee}
                onChange={(event, newUser) => setFormValue(newUser, "employee")}
                clearOnBlur
                clearText={getLocalizedString("clear")}
                noOptionsText={''}
                getOptionLabel={(user) => user.employeeName}
                renderInput={(params) => <TextField required {...params} label={getLocalizedString("employee")} />}
              />
            </Grid>
          }
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <KeyboardDatePicker
                  disabled={!Boolean(formData.employee)}
                  required
                  fullWidth
                  label={getLocalizedString("inDate")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=''
                  minDateMessage=''
                  maxDateMessage=''
                  format="dd/MM/yyyy"
                  margin="none"
                  maxDate={formData.exitDate}
                  value={formData.entryDate}
                  onChange={(e) => setFormValue(e, "entryDate")}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item xs={12} sm={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <KeyboardTimePicker
                  disabled={!Boolean(formData.employee)}
                  required
                  label={getLocalizedString("entryHours")}
                  value={formData.entryTime}
                  onChange={(e) => setFormValue(e, "entryTime")}
                  invalidDateMessage=""
                  minDateMessage=""
                  minutesStep={5}
                  fullWidth
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  format="HH:mm"
                  margin="none"
                  ampm={false}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <KeyboardDatePicker
                  disabled={!Boolean(formData.entryDate)}
                  required
                  fullWidth
                  label={getLocalizedString("outDate")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=''
                  minDateMessage=''
                  maxDateMessage=''
                  format="dd/MM/yyyy"
                  margin="none"
                  minDate={formData.entryDate}
                  maxDate={getNextDay(formData.entryDate)}
                  value={formData.exitDate}
                  onChange={(e) => setFormValue(e, "exitDate")}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item xs={12} sm={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <KeyboardTimePicker
                  disabled={!Boolean(formData.entryDate)}
                  required
                  label={getLocalizedString("exitHours")}
                  value={formData.exitTime}
                  onChange={(e) => setFormValue(e, "exitTime")}
                  invalidDateMessage=""
                  minDateMessage=""
                  minutesStep={5}
                  fullWidth
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  format="HH:mm"
                  margin="none"
                  ampm={false}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
          <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, height: '' }}>
            <TextField
              fullWidth
              multiline
              rows={5}
              margin={"none"}
              label={getLocalizedString("comments")}
              value={formData.comment}
              onChange={(e) => setFormValue(e.target.value, "comment")}
              inputProps={{ maxLength: 100, spellCheck: 'false' }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default AssistanceMarkDialog;